import { AppError, sendFormValidated, sendValidated } from '@shared/api';
import {
  ExpenseDeleteResponseSchema,
  ExpenseDtoSchema,
  PendingExpenseAttachmentUploadResponseSchema,
  PendingExpenseSaveResponseSchema,
} from './schemas';
import {
  toCreateExpenseBody,
  toExpense,
  toGroupExpenseFields,
  toPendingExpenseFields,
  toSavedBill,
} from './mappers';
import type { Expense, NewExpense, SaveBillInput, SavedBill } from '../model/types';

/**
 * Low-level create. Cross-entity cache invalidation (balances live on the group entity)
 * is orchestrated by the feature that USES this — entities never import each other.
 */
export async function createExpense(input: NewExpense): Promise<Expense> {
  const dto = await sendValidated(
    'post',
    `api/groups/${input.groupId}/expenses`,
    toCreateExpenseBody(input),
    ExpenseDtoSchema,
  );
  return toExpense(dto);
}

/**
 * Create or update a pending expense via the real `pending-expense` endpoint (multipart form-data,
 * even with no file — see `sendFormValidated`). PATCHes when `input.id` is set, else POSTs. The
 * backend envelope carries `status` — a `status === false` is a BUSINESS failure (the message is
 * safe to show), surfaced as an `AppError` rather than a silent success.
 */
/**
 * Whether a save uses the GROUP-expense endpoint (+ shape) rather than the pending-expense one — true
 * when the bill belongs to a group AND either it's an EDIT of a committed group-ledger expense
 * (`isGroupExpense`, so it routes there whether or not it still recurs, e.g. after "Stop Repeat") or a
 * new RECURRING expense (a group-bill capability). The receipt upload follows the SAME split, so this
 * is shared by the save + the attachment upload (see `useSaveBill`).
 */
export function usesGroupExpenseEndpoint(input: SaveBillInput): boolean {
  return Boolean(input.groupId) && (input.isGroupExpense === true || Boolean(input.recurrence));
}

export async function savePendingExpense(input: SaveBillInput): Promise<SavedBill> {
  const isUpdate = Boolean(input.id);
  const useGroupEndpoint = usesGroupExpenseEndpoint(input);
  const fields = useGroupEndpoint ? toGroupExpenseFields(input) : toPendingExpenseFields(input);
  // Group edit → PUT /expense/{groupId}/{id}; group create → POST /expense/{groupId}. Pending edit →
  // PATCH /pending-expense/{id}; pending create → POST /pending-expense.
  const method: 'post' | 'put' | 'patch' = useGroupEndpoint
    ? isUpdate
      ? 'put'
      : 'post'
    : isUpdate
      ? 'patch'
      : 'post';
  const url = useGroupEndpoint
    ? isUpdate
      ? `api/v1/expense/${input.groupId}/${input.id}`
      : `api/v1/expense/${input.groupId}`
    : isUpdate
      ? `api/v1/pending-expense/${input.id}`
      : 'api/v1/pending-expense';
  const res = await sendFormValidated(method, url, fields, PendingExpenseSaveResponseSchema);
  if (res.status === false) {
    throw new AppError({
      kind: 'business',
      messageForUser: res.message ?? 'Could not save the expense. Please try again.',
    });
  }
  return toSavedBill(res);
}

/**
 * Delete a committed group-ledger expense — `DELETE /api/v1/expense/{groupId}/{expenseId}` (deletes
 * it for the whole group; the backend keeps it in "Recently Deleted" for restore). A `status === false`
 * envelope is a business failure surfaced as an `AppError` (safe message), not a silent success.
 */
export async function deleteGroupExpense(groupId: string, expenseId: string): Promise<void> {
  const res = await sendValidated(
    'delete',
    `api/v1/expense/${groupId}/${expenseId}`,
    undefined,
    ExpenseDeleteResponseSchema,
  );
  if (res.status === false) {
    throw new AppError({
      kind: 'business',
      messageForUser: res.message ?? 'Could not delete the expense. Please try again.',
    });
  }
}

/**
 * Browsers derive `File.type` from the OS registry, which on Windows has no entry for
 * `.heic`/`.heif` — the pick arrives with an EMPTY type, the multipart part goes out as
 * `application/octet-stream`, and the backend's MIME allow-list rejects it (the same API works
 * from mobile because mobile sets the part's content type explicitly). Rebuild the file with the
 * extension-derived type so the part carries the MIME the backend expects; files that already
 * carry a real type, and unknown extensions, pass through untouched.
 */
const EXTENSION_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
};

function withExplicitMimeType(file: File): File {
  if (file.type && file.type !== 'application/octet-stream') return file;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const mime = EXTENSION_MIME[ext];
  if (!mime) return file;
  return new File([file], file.name, { type: mime, lastModified: file.lastModified });
}

/**
 * Upload a receipt binary for an already-saved pending expense. Multipart with a single `file_url`
 * part (mobile `uploadAttachmentInBackground`). Fired unawaited AFTER create — a failure leaves the
 * expense saved without a receipt, so callers treat it as best-effort. Returns the stored URL (or
 * null). The `pending-expense` create/update deliberately omits the binary; this is the second leg.
 */
export async function uploadPendingExpenseAttachment(
  expenseId: string,
  file: File,
): Promise<string | null> {
  const res = await sendFormValidated(
    'post',
    `api/v1/pending-expense/${expenseId}/attachment/upload`,
    { file_url: withExplicitMimeType(file) },
    PendingExpenseAttachmentUploadResponseSchema,
  );
  return res.data?.file_url ?? null;
}

/**
 * Upload a receipt binary for an already-saved GROUP expense (recurring or committed group-ledger) —
 * `POST /api/v1/expense/{groupId}/{expenseId}/attachment/upload`. Same best-effort second-leg contract
 * as the pending upload; only the endpoint differs (the group-expense collection, not pending-expense).
 */
export async function uploadGroupExpenseAttachment(
  groupId: string,
  expenseId: string,
  file: File,
): Promise<string | null> {
  const res = await sendFormValidated(
    'post',
    `api/v1/expense/${groupId}/${expenseId}/attachment/upload`,
    { file_url: withExplicitMimeType(file) },
    PendingExpenseAttachmentUploadResponseSchema,
  );
  return res.data?.file_url ?? null;
}
